const crypto = require('crypto');
const CryptoJS = require('crypto-js');

class SurveyCakeUtils {
  constructor() {
    this.hashKey = process.env.VUE_APP_SURVEYCAKE_HASH_KEY || '';
    this.ivKey = process.env.VUE_APP_SURVEYCAKE_IV_KEY || '';
    this.apiUrl = process.env.VUE_APP_SURVEYCAKE_API_URL;
  }

  /**
   * AES加密函數
   * @param {string} text - 要加密的文字
   * @param {string} key - 加密金鑰
   * @param {string} iv - 初始化向量
   * @returns {string} 加密後的文字
   */
  encrypt(text, key = this.hashKey, iv = this.ivKey) {
    try {
      const cipher = crypto.createCipher('aes-256-cbc', key);
      let encrypted = cipher.update(text, 'utf8', 'hex');
      encrypted += cipher.final('hex');
      return encrypted;
    } catch (error) {
      console.error('加密失敗:', error);
      return null;
    }
  }

  /**
   * AES解密函數
   * @param {string} encryptedText - 要解密的文字
   * @param {string} key - 解密金鑰
   * @param {string} iv - 初始化向量
   * @returns {string} 解密後的文字
   */
  decrypt(encryptedText, key = this.hashKey, iv = this.ivKey) {
    try {
      const decipher = crypto.createDecipher('aes-256-cbc', key);
      let decrypted = decipher.update(encryptedText, 'hex', 'utf8');
      decrypted += decipher.final('utf8');
      return decrypted;
    } catch (error) {
      console.error('解密失敗:', error);
      return null;
    }
  }

  /**
   * 產生MD5雜湊值
   * @param {string} text - 要雜湊的文字
   * @returns {string} MD5雜湊值
   */
  generateMD5Hash(text) {
    return crypto.createHash('md5').update(text).digest('hex');
  }

  /**
   * 產生SHA256雜湊值
   * @param {string} text - 要雜湊的文字
   * @returns {string} SHA256雜湊值
   */
  generateSHA256Hash(text) {
    return crypto.createHash('sha256').update(text).digest('hex');
  }

  /**
   * 驗證感謝信URL的雜湊值
   * @param {string} svid - Survey ID
   * @param {string} hash - 要驗證的雜湊值
   * @param {string} timestamp - 時間戳記（可選）
   * @returns {boolean} 驗證結果
   */
  validateThankYouHash(svid, hash, timestamp = '') {
    const expectedHash = this.generateThankYouHash(svid, timestamp);
    return expectedHash === hash;
  }

  /**
   * 產生感謝信URL的雜湊值
   * @param {string} svid - Survey ID
   * @param {string} timestamp - 時間戳記（可選）
   * @returns {string} 雜湊值
   */
  generateThankYouHash(svid, timestamp = '') {
    const data = `${svid}${timestamp}${this.hashKey}`;
    return this.generateMD5Hash(data);
  }

  /**
   * 建立完整的感謝信URL
   * @param {string} svid - Survey ID
   * @param {string} baseUrl - 基礎URL（預設為preview.dfcchallenge.org）
   * @returns {string} 完整的感謝信URL
   */
  generateThankYouURL(svid, baseUrl = 'https://preview.dfcchallenge.org') {
    const timestamp = Date.now().toString();
    const hash = this.generateThankYouHash(svid, timestamp);
    return `${baseUrl}/?svid=${svid}&hash=${hash}&t=${timestamp}`;
  }

  /**
   * 解析感謝信URL並驗證
   * @param {string} url - 感謝信URL
   * @returns {object} 解析結果
   */
  parseAndValidateThankYouURL(url) {
    try {
      const urlObj = new URL(url);
      const svid = urlObj.searchParams.get('svid');
      const hash = urlObj.searchParams.get('hash');
      const timestamp = urlObj.searchParams.get('t') || '';

      if (!svid || !hash) {
        return {
          valid: false,
          error: '缺少必要參數',
          svid: null,
          hash: null
        };
      }

      const isValid = this.validateThankYouHash(svid, hash, timestamp);

      return {
        valid: isValid,
        svid: svid,
        hash: hash,
        timestamp: timestamp,
        error: isValid ? null : '雜湊值驗證失敗'
      };
    } catch (error) {
      return {
        valid: false,
        error: `URL解析失敗: ${error.message}`,
        svid: null,
        hash: null
      };
    }
  }

  /**
   * 建立API請求的認證標頭
   * @param {string} svid - Survey ID
   * @param {string} method - HTTP方法
   * @param {object} data - 請求資料（可選）
   * @returns {object} 認證標頭
   */
  createAuthHeaders(svid, method = 'GET', data = {}) {
    const timestamp = Date.now().toString();
    const dataString = typeof data === 'object' ? JSON.stringify(data) : data.toString();
    const signatureData = `${method}${svid}${timestamp}${dataString}${this.hashKey}`;
    const signature = this.generateSHA256Hash(signatureData);

    return {
      'X-Survey-ID': svid,
      'X-Timestamp': timestamp,
      'X-Signature': signature,
      'Content-Type': 'application/json'
    };
  }

  /**
   * 格式化問卷資料
   * @param {object} rawData - 原始問卷資料
   * @returns {object} 格式化後的問卷資料
   */
  formatSurveyData(rawData) {
    if (!rawData) return null;

    return {
      id: rawData.id || rawData.svid,
      title: rawData.title || rawData.name,
      description: rawData.description || '',
      questions: this.formatQuestions(rawData.questions || []),
      responses: this.formatResponses(rawData.responses || []),
      status: rawData.status || 'unknown',
      createdAt: rawData.created_at || rawData.createdAt,
      updatedAt: rawData.updated_at || rawData.updatedAt
    };
  }

  /**
   * 格式化問題資料
   * @param {array} questions - 原始問題陣列
   * @returns {array} 格式化後的問題陣列
   */
  formatQuestions(questions) {
    return questions.map(question => ({
      id: question.id,
      type: question.type,
      title: question.title || question.question,
      required: question.required || false,
      options: question.options || [],
      order: question.order || 0
    }));
  }

  /**
   * 格式化回應資料
   * @param {array} responses - 原始回應陣列
   * @returns {array} 格式化後的回應陣列
   */
  formatResponses(responses) {
    return responses.map(response => ({
      id: response.id,
      questionId: response.question_id || response.questionId,
      answer: response.answer || response.value,
      respondentId: response.respondent_id || response.respondentId,
      submittedAt: response.submitted_at || response.submittedAt
    }));
  }

  /**
   * 前端專用 AES 解密函數（使用 crypto-js）
   * 參考 HTML 範例的解密邏輯
   * @param {string} encryptedData - Base64 編碼的加密數據
   * @param {string} key - 解密金鑰
   * @param {string} iv - 初始化向量
   * @returns {string} 解密後的 JSON 字串
   */
  decryptSurveyCakeData(encryptedData, key = this.hashKey, iv = this.ivKey) {
    try {
      // 建立 CipherParams 物件，將 Base64 編碼的數據解析
      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(encryptedData),
      });

      // 使用 AES 解密
      const decrypted = CryptoJS.AES.decrypt(
        cipherParams,
        CryptoJS.enc.Utf8.parse(key),
        {
          iv: CryptoJS.enc.Utf8.parse(iv),
        }
      );

      // 轉換為 UTF-8 字串
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error('解密結果為空，可能是金鑰或 IV 不正確');
      }

      return decryptedString;
    } catch (error) {
      console.error('SurveyCake 數據解密失敗:', error);
      throw new Error(`解密失敗: ${error.message}`);
    }
  }

  /**
   * 解析並解密 SurveyCake 回應數據
   * @param {string} encryptedData - 加密的數據
   * @returns {object} 解密並格式化後的問卷數據
   */
  parseEncryptedSurveyData(encryptedData) {
    try {
      // 解密數據
      const decryptedString = this.decryptSurveyCakeData(encryptedData);
      
      // 解析 JSON
      const rawData = JSON.parse(decryptedString);
      
      // 格式化數據
      return this.formatSurveyData(rawData);
    } catch (error) {
      console.error('解析加密問卷數據失敗:', error);
      throw new Error(`數據解析失敗: ${error.message}`);
    }
  }
}

export default new SurveyCakeUtils();
